import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { orderAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await orderAPI.getUserOrders();
      setOrders(response.data.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)',
      restaurant_confirmed: 'linear-gradient(135deg, #4ecdc4 0%, #44b8b1 100%)',
      preparing: 'linear-gradient(135deg, #4ecdc4 0%, #44b8b1 100%)',
      ready_for_pickup: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      assigned_delivery: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      picked_up: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      on_the_way: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      delivered: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      cancelled: 'linear-gradient(135deg, #ff6b6b 0%, #e85a5a 100%)'
    };
    return colors[status] || 'linear-gradient(135deg, #666 0%, #888 100%)';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      restaurant_confirmed: '✅',
      preparing: '👨‍🍳',
      ready_for_pickup: '📦',
      assigned_delivery: '🚴',
      picked_up: '📦',
      on_the_way: '🚴',
      delivered: '✅',
      cancelled: '❌'
    };
    return icons[status] || '📋';
  };

  if (loading) return <div className="loading">🔄 Loading your orders...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            📦 My Orders
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Track and manage all your food orders
          </p>
        </div>

        {orders.length === 0 ? (
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '120px', marginBottom: 'var(--space-lg)' }}>🍽️</div>
            <h2 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '700',
              color: 'var(--gray-700)',
              marginBottom: 'var(--space-md)'
            }}>
              No orders yet
            </h2>
            <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-xl)', fontSize: 'var(--font-size-lg)' }}>
              Your order history will appear here
            </p>
            <Link to="/user/home" className="btn btn-primary btn-lg">
              🏪 Browse Restaurants
            </Link>
          </div>
        ) : (
          <div className="grid">
            {orders.map(order => (
              <Link
                key={order._id}
                to={`/user/order/${order._id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className="card fade-in" style={{
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Status Badge - Top Right */}
                  <div style={{
                    position: 'absolute',
                    top: 'var(--space-lg)',
                    right: 'var(--space-lg)',
                    padding: 'var(--space-sm) var(--space-lg)',
                    borderRadius: 'var(--radius-full)',
                    background: getStatusColor(order.status),
                    color: 'white',
                    fontWeight: '700',
                    fontSize: 'var(--font-size-xs)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-xs)',
                    boxShadow: 'var(--shadow-md)'
                  }}>
                    <span>{getStatusIcon(order.status)}</span>
                    <span>{order.status.replace(/_/g, ' ')}</span>
                  </div>

                  {/* Restaurant Info */}
                  <div style={{ marginBottom: 'var(--space-lg)', paddingRight: '120px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-md)',
                      marginBottom: 'var(--space-md)'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'var(--gradient-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px'
                      }}>
                        🏪
                      </div>
                      <div>
                        <h3 style={{
                          fontSize: 'var(--font-size-2xl)',
                          fontWeight: '700',
                          marginBottom: 'var(--space-xs)',
                          color: 'var(--gray-900)'
                        }}>
                          {order.restaurantId.name}
                        </h3>
                        <p style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--gray-600)',
                          margin: 0
                        }}>
                          Order #{order.orderNumber}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Order Details */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 'var(--space-md)',
                    marginBottom: 'var(--space-lg)',
                    paddingBottom: 'var(--space-lg)',
                    borderBottom: '2px solid var(--gray-100)'
                  }}>
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(78, 205, 196, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid rgba(78, 205, 196, 0.2)'
                    }}>
                      <p style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-600)',
                        marginBottom: 'var(--space-xs)',
                        fontWeight: '600'
                      }}>
                        ITEMS
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: '800',
                        color: 'var(--secondary)',
                        margin: 0
                      }}>
                        {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
                      </p>
                    </div>
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(255, 107, 107, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid rgba(255, 107, 107, 0.2)'
                    }}>
                      <p style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-600)',
                        marginBottom: 'var(--space-xs)',
                        fontWeight: '600'
                      }}>
                        TOTAL
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: '800',
                        color: 'var(--primary)',
                        margin: 0
                      }}>
                        ₹{order.pricing.total}
                      </p>
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    color: 'var(--gray-600)',
                    fontSize: 'var(--font-size-sm)',
                    marginTop: 'auto'
                  }}>
                    <span>🕒</span>
                    <span>{new Date(order.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                  </div>

                  {/* Hover Accent */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '150px',
                    height: '150px',
                    background: 'radial-gradient(circle at bottom left, rgba(78, 205, 196, 0.1) 0%, transparent 70%)',
                    pointerEvents: 'none'
                  }} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyOrders;
